import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { formatError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2 } from "lucide-react";
import { LogoHorizontal } from "../components/Logo";
import { useSeo } from "../lib/seo";

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="p-8 pb-4">
        <Link to="/">
          <LogoHorizontal size={92} />
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            <h1 className="font-heading text-2xl font-bold text-slate-900">{title}</h1>
            <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
            <div className="mt-6">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Login() {
  useSeo({ title: "Log in | DentiCision", path: "/login", noindex: true });
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = (params.get("next") || "").startsWith("/app") ? params.get("next") : "/app";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate(next);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Sign in to your clinical account">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" placeholder="you@clinic.com" />
        </div>
        <div>
          <Label htmlFor="password">Password</Label>
          <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5" placeholder="••••••••" />
        </div>
        {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
        <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 h-11">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
        </Button>
      </form>
      <div className="flex items-center justify-between mt-5 text-sm">
        <Link to="/forgot-password" className="text-sky-600 hover:underline" data-testid="forgot-link">Forgot password?</Link>
        <Link to={`/register${next !== "/app" ? `?next=${encodeURIComponent(next)}` : ""}`} className="text-slate-600 hover:underline" data-testid="to-register-link">Create account</Link>
      </div>
    </AuthShell>
  );
}
